import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface StoredCartItem {
  item: number;
  codigoProduto: string;
  quantidadeProduto: number;
  armazem: string;
}

@Injectable({
  providedIn: 'root'
})
export class CartStorageService {
  private readonly cartKey = 'carrinhoCompras';
  private readonly quantitySubject = new BehaviorSubject<number>(
    this.calculateQuantity()
  );

  readonly quantity$ = this.quantitySubject.asObservable();

  constructor() {
    // Remove o carrinho persistente criado por versões anteriores.
    localStorage.removeItem(this.cartKey);
  }

  getItems(): StoredCartItem[] {
    const storedCart = sessionStorage.getItem(this.cartKey);
    if (!storedCart) {
      return [];
    }

    try {
      const items = JSON.parse(storedCart);
      return Array.isArray(items) ? items : [];
    } catch {
      sessionStorage.removeItem(this.cartKey);
      return [];
    }
  }

  addItem(
    productCode: string,
    quantity: number,
    warehouse: string
  ): boolean {
    const normalizedQuantity = Number(quantity);
    if (!productCode || !warehouse || !Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) {
      return false;
    }

    const items = this.getItems();
    const nextItem = items.reduce(
      (highest, item) => Math.max(highest, Number(item.item) || 0),
      0
    ) + 1;

    items.push({
      item: nextItem,
      codigoProduto: productCode,
      quantidadeProduto: normalizedQuantity,
      armazem: warehouse
    });

    this.save(items);
    return true;
  }

  removeItem(itemId: number): boolean {
    const items = this.getItems();
    const filteredItems = items.filter(item => item.item !== itemId);

    if (filteredItems.length === items.length) {
      return false;
    }

    this.save(filteredItems);
    return true;
  }

  clearCart(): void {
    sessionStorage.removeItem(this.cartKey);
    this.quantitySubject.next(0);
  }

  getQuantity(): number {
    return this.calculateQuantity();
  }

  private save(items: StoredCartItem[]): void {
    sessionStorage.setItem(this.cartKey, JSON.stringify(items));
    this.quantitySubject.next(this.calculateQuantity());
  }

  private calculateQuantity(): number {
    return this.getItems().reduce(
      (total, item) => total + Math.max(0, Number(item.quantidadeProduto) || 0),
      0
    );
  }
}
